import { formatShortDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { type SxProps, type Theme } from "@mui/material";
import type { IconType } from "@src/appV2/redesign/system/ui-components";

import { type MissedPunchRequestStatus } from "../../types";

export interface FooterDataProps {
  missedPunchRequestStatus: MissedPunchRequestStatus;
  isVerified: boolean;
  isFullShiftPayment: boolean;
  isMissedPunchRequestEnabled: boolean;
  isTimesheetUploaded: boolean;
  canSubmitTimesheet: boolean;
  requestExpiryTime?: string;
  facilityTimeZone: string;
  isAcceptingOriginalTimes?: boolean;
  isCheckingGeofence?: boolean;
}

export interface FooterActionCallbacks {
  onSubmitNewRequest: () => void;
  onAcceptOriginalTimes: () => void;
  onUploadTimesheet: () => void;
  onContactSupport: () => void;
}

export interface FooterContent {
  wrapperSx: SxProps<Theme>;
  alertTitle?: string;
  description: string;
  descriptionVariant: "body2" | "caption";
  primaryButtonLabel: string;
  primaryButtonVariant: "contained" | "outlined";
  primaryButtonDisabled: boolean;
  primaryButtonStartIconType?: IconType;
  primaryButtonAction?: () => void;
  isPrimaryButtonLoading?: boolean;
  secondaryButtonLabel?: string;
  secondaryButtonAction?: () => void;
  isSecondaryButtonLoading?: boolean;
}

const actionableStyle = {
  wrapperSx: (theme: Theme) => ({
    backgroundColor: theme.palette.intent?.error.background,
  }),
  descriptionVariant: "body2" as const,
  primaryButtonVariant: "outlined" as const,
  primaryButtonDisabled: false,
};

const informationalStyle = {
  wrapperSx: (theme: Theme) => ({
    backgroundColor: theme.palette.background.tertiary,
  }),
  descriptionVariant: "caption" as const,
  primaryButtonVariant: "outlined" as const,
  primaryButtonDisabled: false,
};

function getPendingFooterContent(data: FooterDataProps): FooterContent {
  const { isFullShiftPayment, requestExpiryTime, facilityTimeZone } = data;
  const requestType = isFullShiftPayment ? "shift payment request" : "pay adjustment request";
  const deadlineText = isDefined(requestExpiryTime)
    ? ` You'll receive a response by ${formatShortDate(requestExpiryTime, {
        timeZone: facilityTimeZone,
      })}.`
    : "";

  return {
    ...informationalStyle,
    description: `Your timesheet has been submitted and your ${requestType} is under review.${deadlineText}`,
    primaryButtonLabel: isFullShiftPayment
      ? "Shift payment pending approval"
      : "Pay adjustment pending approval",
    primaryButtonVariant: "contained",
    primaryButtonDisabled: true,
    primaryButtonStartIconType: "clock",
  };
}

function getExpiredFooterContent(
  data: FooterDataProps,
  actions: FooterActionCallbacks
): FooterContent {
  const { isFullShiftPayment, canSubmitTimesheet, isCheckingGeofence } = data;
  const { onSubmitNewRequest, onContactSupport } = actions;
  const alertTitle = isFullShiftPayment
    ? "Shift payment request expired"
    : "Pay adjustment request expired";

  if (canSubmitTimesheet) {
    return {
      ...actionableStyle,
      alertTitle,
      description:
        "This request wasn't reviewed and may have been missed. You may submit another request if you believe your timesheet still needs correction.",
      primaryButtonLabel: "Submit new request",
      primaryButtonAction: onSubmitNewRequest,
      isPrimaryButtonLoading: isCheckingGeofence,
      secondaryButtonLabel: "Chat with support",
      secondaryButtonAction: onContactSupport,
    };
  }

  return {
    ...actionableStyle,
    alertTitle,
    description:
      "This request wasn't reviewed and may have been missed. Please contact support for assistance.",
    primaryButtonLabel: "Chat with support",
    primaryButtonAction: onContactSupport,
  };
}

function getDeclinedFooterContent(
  data: FooterDataProps,
  actions: FooterActionCallbacks
): FooterContent {
  const { isFullShiftPayment, canSubmitTimesheet, isAcceptingOriginalTimes, isCheckingGeofence } =
    data;
  const { onSubmitNewRequest, onAcceptOriginalTimes, onContactSupport } = actions;

  const alertTitle = isFullShiftPayment
    ? "Shift payment request declined"
    : "Pay adjustment request declined";

  if (canSubmitTimesheet) {
    return {
      ...actionableStyle,
      alertTitle,
      description:
        "If you believe your verified times are incorrect, you may submit another request.",
      primaryButtonLabel: "Submit new request",
      primaryButtonAction: onSubmitNewRequest,
      isPrimaryButtonLoading: isCheckingGeofence,
      secondaryButtonLabel: isFullShiftPayment ? undefined : "Accept original times",
      secondaryButtonAction: isFullShiftPayment ? undefined : onAcceptOriginalTimes,
      isSecondaryButtonLoading: isFullShiftPayment ? undefined : isAcceptingOriginalTimes,
    };
  }

  return {
    ...actionableStyle,
    alertTitle,
    description: "If you believe your verified times are incorrect, you may contact support.",
    primaryButtonLabel: "Contact support",
    primaryButtonAction: onContactSupport,
    secondaryButtonLabel: isFullShiftPayment ? undefined : "Accept original times",
    secondaryButtonAction: isFullShiftPayment ? undefined : onAcceptOriginalTimes,
    isSecondaryButtonLoading: isFullShiftPayment ? undefined : isAcceptingOriginalTimes,
  };
}

function getUnverifiedFooterContent(
  data: FooterDataProps,
  actions: FooterActionCallbacks
): FooterContent {
  const {
    isMissedPunchRequestEnabled,
    isTimesheetUploaded,
    canSubmitTimesheet,
    isCheckingGeofence,
  } = data;
  const { onSubmitNewRequest, onUploadTimesheet, onContactSupport } = actions;

  if (!canSubmitTimesheet) {
    return {
      ...actionableStyle,
      alertTitle: "Missing timesheet",
      description:
        "Looks like you forgot to clock out. Please contact support to get paid for this shift.",
      primaryButtonLabel: "Contact support",
      primaryButtonAction: onContactSupport,
    };
  }

  if (!isMissedPunchRequestEnabled && isTimesheetUploaded) {
    return {
      ...informationalStyle,
      description:
        "A timesheet has already been uploaded and not yet approved. Please re-upload if you wish to make changes.",
      primaryButtonLabel: "Reupload timesheet",
      primaryButtonAction: onUploadTimesheet,
    };
  }

  if (isMissedPunchRequestEnabled) {
    return {
      ...actionableStyle,
      alertTitle: "Missing timesheet",
      description:
        "Looks like you forgot to clock out. Please submit your timesheet to get paid for this shift.",
      primaryButtonLabel: "Submit timesheet",
      primaryButtonAction: onSubmitNewRequest,
      isPrimaryButtonLoading: isCheckingGeofence,
    };
  }

  return {
    ...actionableStyle,
    alertTitle: "Missing timesheet",
    description:
      "Looks like you forgot to clock out. Please upload your timesheet to get paid for this shift.",
    primaryButtonLabel: "Upload timesheet",
    primaryButtonAction: onUploadTimesheet,
  };
}

export function getFooterContent(
  data: FooterDataProps,
  actions: FooterActionCallbacks
): FooterContent | undefined {
  const { missedPunchRequestStatus, isVerified } = data;

  if (missedPunchRequestStatus === "pending") {
    return getPendingFooterContent(data);
  }

  if (missedPunchRequestStatus === "expired") {
    return getExpiredFooterContent(data, actions);
  }

  if (missedPunchRequestStatus === "declined") {
    return getDeclinedFooterContent(data, actions);
  }

  if (isVerified) {
    return undefined;
  }

  return getUnverifiedFooterContent(data, actions);
}
